import type { FlowEditorNode, FlowEditorState, FlowGridItem, FlowGridLayouts, FlowNodeKind } from "./types";
import { RGL_CONTAINER_PADDING, viewportPresets } from "./constants";
import { getFlowNodeSize } from "./flowNodeDefaults";
import { getDefaultGridSize, getDefaultGridSizeForSettings, getFlowViewport, getGridColumnWidth, getGridConfig, getGridRows } from "./flowViewportUtils";
export const clampGridItem = (
  item: FlowGridItem,
  viewport: FlowEditorState["viewport"],
): FlowGridItem => {
  const config = getGridConfig(viewport);
  const w = Math.min(Math.max(Math.round(item.w) || 1, 1), config.cols);
  const rows = getGridRows(viewport);
  const h = Math.min(Math.max(Math.round(item.h) || 1, 1), rows);

  return {
    x: Math.min(Math.max(Math.round(item.x) || 0, 0), config.cols - w),
    y: Math.min(Math.max(Math.round(item.y) || 0, 0), rows - h),
    w,
    h,
  };
};

export const getCenteredGridItem = (
  node: Pick<FlowEditorNode, "data">,
  viewport: FlowEditorState["viewport"],
): FlowGridItem => {
  const size =
    node.data.layouts?.[viewport.device] ||
    getDefaultGridSize(node.data.kind || "text", viewport.device);
  const item = clampGridItem({ x: 0, y: 0, w: size.w, h: size.h }, viewport);
  const rows = getGridRows(viewport);

  return {
    ...item,
    x: Math.max(
      Math.floor((getGridConfig(viewport.device).cols - item.w) / 2),
      0,
    ),
    y: Math.max(Math.floor((rows - item.h) / 2), 0),
  };
};

export const getBottomLeftGridItem = (
  node: Pick<FlowEditorNode, "data">,
  viewport: FlowEditorState["viewport"],
): FlowGridItem => {
  const size =
    node.data.layouts?.[viewport.device] ||
    getDefaultGridSize(node.data.kind || "text", viewport.device);
  const item = clampGridItem({ x: 0, y: 0, w: size.w, h: size.h }, viewport);
  const rows = getGridRows(viewport);

  return {
    ...item,
    x: 0,
    y: Math.max(rows - item.h, 0),
  };
};

export const getGridItemFromPixel = (
  node: FlowEditorNode,
  viewport: FlowEditorState["viewport"],
): FlowGridItem => {
  const config = getGridConfig(viewport);
  const colWidth = getGridColumnWidth(viewport);
  const fallbackSize = getDefaultGridSizeForSettings(
    node.data.kind || "text",
    viewport.device,
    node.data.settings,
  );
  const x = Math.round(
    (node.position.x - config.margin) / (colWidth + config.margin),
  );
  const y = Math.round(
    (node.position.y - config.margin) / (config.rowHeight + config.margin),
  );

  return clampGridItem(
    {
      x,
      y,
      w: fallbackSize.w,
      h: fallbackSize.h,
    },
    viewport,
  );
};

export const getNodeGridItem = (
  node: FlowEditorNode,
  viewport: FlowEditorState["viewport"],
): FlowGridItem => {
  const layout =
    node.data.layouts?.[viewport.device] ||
    node.data.layouts?.lg ||
    getGridItemFromPixel(node, viewport);
  const clampedLayout = clampGridItem(layout, viewport);
  const kind = node.data.kind || "text";

  if (kind !== "alert") return clampedLayout;

  const minimumSize = getDefaultGridSizeForSettings(
    kind,
    viewport.device,
    node.data.settings,
  );

  return clampGridItem(
    {
      ...clampedLayout,
      w: Math.max(clampedLayout.w, minimumSize.w),
      h: Math.max(clampedLayout.h, minimumSize.h),
    },
    viewport,
  );
};

export const getGridItemPixels = (
  item: FlowGridItem,
  viewport: FlowEditorState["viewport"],
) => {
  const config = getGridConfig(viewport);
  const colWidth = getGridColumnWidth(viewport);

  return {
    left: RGL_CONTAINER_PADDING[0] + item.x * (colWidth + config.margin),
    top: RGL_CONTAINER_PADDING[1] + item.y * (config.rowHeight + config.margin),
    width: item.w * colWidth + Math.max(item.w - 1, 0) * config.margin,
    height: item.h * config.rowHeight + Math.max(item.h - 1, 0) * config.margin,
  };
};

export const getPreferredGridWidth = (
  node: Pick<FlowEditorNode, "data">,
  viewport: FlowEditorState["viewport"],
) => {
  const config = getGridConfig(viewport.device);
  const colWidth = getGridColumnWidth(viewport);
  const kind = node.data.kind || "text";
  const targetWidth = Math.min(
    getFlowNodeSize(kind).width,
    viewport.width - RGL_CONTAINER_PADDING[0] * 2,
  );

  return Math.min(
    Math.max(
      Math.ceil((targetWidth + config.margin) / (colWidth + config.margin)),
      1,
    ),
    config.cols,
  );
};

export const areGridItemsColliding = (a: FlowGridItem, b: FlowGridItem) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

export const resolveGridCollision = (
  nodes: FlowEditorNode[],
  nodeId: string,
  nextItem: FlowGridItem,
  viewport: FlowEditorState["viewport"],
) => {
  let item = clampGridItem(nextItem, viewport);
  const otherItems = nodes
    .filter((node) => node.id !== nodeId)
    .map((node) => getNodeGridItem(node, viewport));

  while (otherItems.some((other) => areGridItemsColliding(item, other))) {
    item = {
      ...item,
      y: item.y + 1,
    };
  }

  return item;
};

export const setNodeLayout = (
  node: FlowEditorNode,
  viewport: FlowEditorState["viewport"],
  item: FlowGridItem,
) => {
  const nextItem = clampGridItem(item, viewport);
  const pixels = getGridItemPixels(nextItem, viewport);

  return {
    ...node,
    position: { x: pixels.left, y: pixels.top },
    data: {
      ...node.data,
      width: pixels.width,
      height: pixels.height,
      layouts: {
        ...(node.data.layouts || {}),
        [viewport.device]: nextItem,
      },
    },
  };
};

export const createDefaultGridLayouts = (
  kind: FlowNodeKind,
  settings?: Record<string, any>,
): FlowGridLayouts =>
  viewportPresets.reduce((layouts, preset) => {
    const viewport = getFlowViewport({ device: preset.key });
    const size = getDefaultGridSizeForSettings(kind, preset.key, settings);
    const rows = getGridRows(viewport);
    const item = clampGridItem(
      {
        x: Math.max(
          Math.floor((getGridConfig(preset.key).cols - size.w) / 2),
          0,
        ),
        y: Math.max(Math.floor((rows - size.h) / 2), 0),
        w: size.w,
        h: size.h,
      },
      viewport,
    );

    return {
      ...layouts,
      [preset.key]: item,
    };
  }, {} as FlowGridLayouts);
